/**
 * Email Composer Component
 * Composes and sends emails with template support.
 * Supports visual WYSIWYG editing and HTML preview (Bigin-style).
 */

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, X, FileText, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmailComposerProps {
  contactId?: string;
  dealId?: string;
  to?: Array<{ name?: string; email: string }>;
  replySubject?: string; // Original subject for reply
  replyOriginalMessage?: string; // Original message content for reply
  /**
   * CRM `emails.id` of the message being replied to. Backend uses it to build
   * In-Reply-To / References headers so the outgoing message threads with the parent.
   */
  parentEmailId?: string;
  /**
   * Existing CRM `email_threads.id` to attach the new message to. Passed as a fallback when
   * no `parentEmailId` is available (e.g. new message from an existing thread's compose area).
   */
  threadId?: string;
  /** Pre-fill subject (e.g. when composing from contract email button) */
  initialSubject?: string;
  /** Pre-fill body HTML (e.g. template for Zmluva o dielo) */
  initialBody?: string;
  /** Pre-attach files (e.g. contract + deposit + quote PDFs) */
  initialAttachmentFiles?: File[];
  /** Render inline without Dialog (e.g. inside a side Sheet). */
  embedded?: boolean;
  onClose: () => void;
  onSent: () => void;
}

export default function EmailComposer({
  contactId,
  dealId,
  to = [],
  replySubject,
  replyOriginalMessage,
  parentEmailId,
  threadId,
  initialSubject,
  initialBody,
  initialAttachmentFiles,
  embedded = false,
  onClose,
  onSent,
}: EmailComposerProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [toList, setToList] = useState<Array<{ name?: string; email: string }>>(to);
  const [ccList, setCcList] = useState<Array<{ name?: string; email: string }>>([]);
  const [showCcRow, setShowCcRow] = useState(false);
  const [subject, setSubject] = useState(initialSubject ?? "");
  const [htmlBody, setHtmlBody] = useState(initialBody ?? "");
  const [attachments, setAttachments] = useState<File[]>(initialAttachmentFiles ?? []);
  const qc = useQueryClient();
  const { t } = useTranslation();
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const syncFromStateRef = useRef(false);

  // Initialize subject with "Re: " prefix if replying
  useEffect(() => {
    if (replySubject) {
      const replyPrefix = "Re: ";
      if (!replySubject.toLowerCase().startsWith(replyPrefix.toLowerCase())) {
        setSubject(replyPrefix + replySubject);
      } else {
        setSubject(replySubject);
      }
    }
  }, [replySubject]);

  // Apply initial subject/body when provided (e.g. contract email). Attachments already set in useState.
  useEffect(() => {
    if (initialSubject != null && initialSubject !== "" && !replySubject) setSubject(initialSubject);
    if (initialBody != null && initialBody !== "") {
      setHtmlBody(initialBody);
      syncFromStateRef.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  // Sync htmlBody into contentEditable when it was set from template or initial (not from user typing).
  useEffect(() => {
    if (!syncFromStateRef.current || !contentEditableRef.current) return;
    contentEditableRef.current.innerHTML = htmlBody || "<p></p>";
    syncFromStateRef.current = false;
  }, [htmlBody]);

  // Fetch templates
  const templatesQuery = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const res = await fetch(apiUrl("/api/email/templates"), {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch templates");
      const data = await res.json();
      return data.templates as Array<{
        id: string;
        name: string;
        subject_template: string;
        html_template: string;
        text_template?: string;
      }>;
    },
  });

  // Report client error to backend for production debugging (fire-and-forget)
  const reportClientError = (
    message: string,
    stack: string | undefined,
    data: Record<string, unknown>
  ) => {
    fetch(apiUrl("/api/client-error"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        message,
        stack,
        operation: "email_send",
        data,
      }),
    }).catch(() => {});
  };

  // Send email mutation
  const sendMutation = useMutation({
    mutationFn: async () => {
      const context = {
        attachmentCount: attachments.length,
        totalAttachmentBytes: attachments.reduce((s, f) => s + f.size, 0),
      };
      try {
        if (toList.length === 0) {
          throw new Error("Legalább egy címzett szükséges");
        }
        if (!subject) {
          throw new Error("Tárgy megadása kötelező");
        }
        if (!htmlBody) {
          throw new Error("Üzenet megadása kötelező");
        }

        // Convert attachments to base64 without ever exceeding JS engine argument limits (no apply/spread)
        const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
          const bytes = new Uint8Array(buffer);
          const len = bytes.length;
          const CHUNK = 8192;
          let binary = "";
          for (let i = 0; i < len; i += CHUNK) {
            const end = Math.min(i + CHUNK, len);
            for (let j = i; j < end; j++) {
              binary += String.fromCharCode(bytes[j]!);
            }
          }
          return btoa(binary);
        };

        let attachmentData: Array<{
          filename: string;
          mime_type: string;
          size_bytes: number;
          file_data: string;
          storage_url: string;
        }>;
        try {
          attachmentData = await Promise.all(
            attachments.map(async (file) => {
              const arrayBuffer = await file.arrayBuffer();
              const base64 = arrayBufferToBase64(arrayBuffer);
              return {
                filename: file.name,
                mime_type: file.type || "application/octet-stream",
                size_bytes: file.size,
                file_data: base64,
                storage_url: "",
              };
            })
          );
        } catch (e) {
          const err = e instanceof Error ? e : new Error(String(e));
          reportClientError(err.message, err.stack, { ...context, step: "base64" });
          throw e;
        }

        let bodyStr: string;
        try {
          bodyStr = JSON.stringify({
            to: toList,
            cc: ccList.length > 0 ? ccList : undefined,
            subject,
            html_body: htmlBody,
            contactId,
            dealId,
            parentEmailId: parentEmailId || undefined,
            threadId: threadId || undefined,
            templateId: selectedTemplateId || undefined,
            attachments: attachmentData.length > 0 ? attachmentData : undefined,
          });
        } catch (e) {
          const err = e instanceof Error ? e : new Error(String(e));
          reportClientError(err.message, err.stack, { ...context, step: "stringify" });
          throw e;
        }

        const res = await fetch(apiUrl("/api/email/send"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: bodyStr,
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || "Email küldése sikertelen");
        }

        return res.json();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        reportClientError(err.message, err.stack, context);
        if (import.meta.env.DEV) {
          console.error("[EmailComposer] send error:", err.message, err.stack, context);
        }
        throw e;
      }
    },
    onSuccess: () => {
      onSent();
      qc.invalidateQueries({ queryKey: ["email-threads"] });
    },
  });

  // Handle template selection – load HTML and sync into contentEditable; "none" clears body (Bigin-style)
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId === "" || templateId === "none") {
      setHtmlBody("");
      syncFromStateRef.current = true;
      return;
    }
    const template = templatesQuery.data?.find((t) => t.id === templateId);
    if (template) {
      if (!replySubject) setSubject(template.subject_template);
      setHtmlBody(template.html_template);
      syncFromStateRef.current = true;
    }
  };

  // Add recipient
  const addRecipient = (type: "to" | "cc") => {
    const email = prompt("Email cím:");
    if (email) {
      const parsed = parseEmail(email);
      if (type === "to") {
        setToList([...toList, parsed]);
      } else {
        setCcList([...ccList, parsed]);
      }
    }
  };

  // Remove recipient
  const removeRecipient = (type: "to" | "cc", index: number) => {
    if (type === "to") {
      setToList(toList.filter((_, i) => i !== index));
    } else {
      setCcList(ccList.filter((_, i) => i !== index));
    }
  };

  // Parse email address
  const parseEmail = (email: string): { name?: string; email: string } => {
    const match = email.match(/^(.+?)\s*<(.+?)>$|^(.+?)$/);
    if (match) {
      if (match[2]) {
        return { name: match[1].trim(), email: match[2].trim().toLowerCase() };
      }
      return { email: (match[3] || match[1]).trim().toLowerCase() };
    }
    return { email: email.trim().toLowerCase() };
  };

  const content = (
    <>
        {/* Header: title left, Template + Cancel right (Bigin-style) */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex flex-row items-start justify-between gap-4">
          <div>
            <DialogTitle>{replySubject ? "Válasz" : "Új email"}</DialogTitle>
            <DialogDescription>
              {replySubject ? "Válasz küldése" : "Írj és küldj emailt sablonnal vagy anélkül"}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {templatesQuery.data && templatesQuery.data.length > 0 && (
              <Select
                value={selectedTemplateId || "none"}
                onValueChange={(value) => {
                  if (value === "none") handleTemplateSelect("");
                  else handleTemplateSelect(value);
                }}
              >
                <SelectTrigger className="w-[220px] h-9">
                  <SelectValue placeholder={t("emailExtra.selectTemplatePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("emailExtra.noTemplate")}</SelectItem>
                  {templatesQuery.data.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className={cn("flex-1", embedded && "min-h-0")}>
          <div className="flex flex-col h-full">
            {/* Email fields - Apple Mail style */}
            <div className="border-b">
          {/* To + Másolat gomb a sor végén */}
              <div className="flex items-center border-b border-border/50 min-h-[44px]">
                <div className="w-20 px-4 text-sm text-muted-foreground font-medium">Címzett:</div>
                <div className="flex-1 flex items-center gap-2 py-2 pr-2">
                  {toList.length === 0 ? (
                    <Input
                      type="text"
                      placeholder={t("emailExtra.emailPlaceholder")}
                      className="border-0 shadow-none focus-visible:ring-0 h-auto p-0"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.currentTarget.value) {
                          e.preventDefault();
                          const parsed = parseEmail(e.currentTarget.value);
                          setToList([parsed]);
                          e.currentTarget.value = "";
                        }
                      }}
                      onBlur={(e) => {
                        if (e.currentTarget.value) {
                          const parsed = parseEmail(e.currentTarget.value);
                          setToList([parsed]);
                          e.currentTarget.value = "";
                        }
                      }}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-1 items-center flex-1">
              {toList.map((recipient, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1 h-7">
                  {recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email}
                  <button
                    onClick={() => removeRecipient("to", index)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
                      <Input
                        type="text"
                        placeholder={t("emailExtra.emailPlaceholder")}
                        className="border-0 shadow-none focus-visible:ring-0 h-7 p-0 flex-1 min-w-[200px]"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.currentTarget.value) {
                            e.preventDefault();
                            const parsed = parseEmail(e.currentTarget.value);
                            setToList([...toList, parsed]);
                            e.currentTarget.value = "";
                          }
                        }}
                        onBlur={(e) => {
                          if (e.currentTarget.value) {
                            const parsed = parseEmail(e.currentTarget.value);
                            setToList([...toList, parsed]);
                            e.currentTarget.value = "";
                          }
                        }}
                      />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 text-muted-foreground"
                    onClick={() => setShowCcRow(true)}
                  >
                    Másolat
                  </Button>
                </div>
              </div>

          {/* CC – csak ha a Másolat gombra kattintottak */}
              {showCcRow && (
              <div className="flex items-center border-b border-border/50 min-h-[44px]">
                <div className="w-20 px-4 text-sm text-muted-foreground font-medium">Másolat:</div>
                <div className="flex-1 flex items-center gap-2 py-2 pr-4">
                  {ccList.length === 0 ? (
                    <Input
                      type="text"
                      placeholder={t("emailExtra.emailPlaceholder")}
                      className="border-0 shadow-none focus-visible:ring-0 h-auto p-0"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.currentTarget.value) {
                          e.preventDefault();
                          const parsed = parseEmail(e.currentTarget.value);
                          setCcList([parsed]);
                          e.currentTarget.value = "";
                        }
                      }}
                      onBlur={(e) => {
                        if (e.currentTarget.value) {
                          const parsed = parseEmail(e.currentTarget.value);
                          setCcList([parsed]);
                          e.currentTarget.value = "";
                        }
                      }}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-1 items-center flex-1">
              {ccList.map((recipient, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1 h-7">
                  {recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email}
                  <button
                    onClick={() => removeRecipient("cc", index)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
                      <Input
                        type="text"
                        placeholder={t("emailExtra.emailPlaceholder")}
                        className="border-0 shadow-none focus-visible:ring-0 h-7 p-0 flex-1 min-w-[200px]"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.currentTarget.value) {
                            e.preventDefault();
                            const parsed = parseEmail(e.currentTarget.value);
                            setCcList([...ccList, parsed]);
                            e.currentTarget.value = "";
                          }
                        }}
                        onBlur={(e) => {
                          if (e.currentTarget.value) {
                            const parsed = parseEmail(e.currentTarget.value);
                            setCcList([...ccList, parsed]);
                            e.currentTarget.value = "";
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              )}

          {/* Subject */}
              <div className="flex items-center border-b border-border/50 min-h-[44px]">
                <div className="w-20 px-4 text-sm text-muted-foreground font-medium">Tárgy:</div>
                <div className="flex-1 pr-4">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("emailExtra.subjectPlaceholder")}
                    className="border-0 shadow-none focus-visible:ring-0 h-auto p-0"
            />
          </div>
              </div>

              {/* From (Sender) – text-foreground a sötét módban látható szöveghez */}
              <div className="flex items-center border-b border-border/50 min-h-[44px]">
                <div className="w-20 px-4 text-sm text-muted-foreground font-medium">Feladó:</div>
                <div className="flex-1 pr-4 text-sm text-foreground">
                  info@tptbrany.sk - info@tptbrany.sk
                </div>
              </div>

            </div>

            {/* Body and content area */}
            <div className="flex-1 flex flex-col min-h-0">
            {/* Original message (for replies) */}
            {replyOriginalMessage && (
              <div className="px-4 py-3 border-b bg-muted/10">
                <div className="text-xs font-semibold mb-2 text-muted-foreground">Eredeti üzenet:</div>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {replyOriginalMessage}
                </div>
              </div>
            )}

            {/* Body: egyetlen szerkeszthető előnézet – kattintásra a szöveg szerkeszthető */}
            <div className="flex-1 flex flex-col min-h-0 px-4">
              <div
                ref={contentEditableRef}
                contentEditable
                suppressContentEditableWarning
                className={cn(
                  "border rounded-lg p-4 bg-white text-gray-900 overflow-auto outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 empty:before:content-[attr(data-placeholder)] before:text-muted-foreground",
                  embedded ? "min-h-[200px] flex-1" : "min-h-[300px]",
                )}
                style={embedded ? undefined : { minHeight: "min(60vh, 600px)" }}
                data-placeholder={t("emailExtra.writeMessagePlaceholder")}
                onInput={() => {
                  if (contentEditableRef.current) setHtmlBody(contentEditableRef.current.innerHTML);
                }}
                onBlur={() => {
                  if (contentEditableRef.current) setHtmlBody(contentEditableRef.current.innerHTML);
                }}
              />
            </div>
            </div>

            {/* Error – a tartalom fölött */}
            {sendMutation.error && (
              <div className="px-4 py-2 text-sm text-destructive bg-destructive/10 mx-4">
                {sendMutation.error.message}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Fix sáv: Melléklet gomb fix méret, alatta a lista; fájlnév rövidítve */}
        <div className="border-t bg-muted/30 px-4 py-3 flex items-end justify-between gap-4 shrink-0">
          <div className="flex flex-col gap-2 min-w-0 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit shrink-0"
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.multiple = true;
                input.onchange = (e) => {
                  const files = (e.target as HTMLInputElement).files;
                  if (files) setAttachments((a) => [...a, ...Array.from(files)]);
                };
                input.click();
              }}
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Melléklet
            </Button>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 max-w-[50vw]">
                {attachments.map((file, index) => {
                  const maxNameLen = 40;
                  const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
                  const displayName =
                    file.name.length <= maxNameLen
                      ? file.name
                      : file.name.slice(0, maxNameLen - ext.length - 1) + "…" + ext;
                  return (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1 max-w-full">
                      <FileText className="h-3 w-3 shrink-0" />
                      <span className="truncate" title={file.name}>
                        {displayName}
                      </span>
                      <span className="shrink-0 text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                      <button
                        onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}
                        className="ml-1 hover:text-destructive shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
          <Button
            onClick={() => sendMutation.mutate()}
            disabled={sendMutation.isPending}
            size="sm"
            className="shrink-0"
          >
            <Send className="h-4 w-4 mr-2" />
            {sendMutation.isPending ? "Küldés..." : "Küldés"}
          </Button>
        </div>
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0 h-full overflow-hidden">
        {content}
      </div>
    );
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[90vw] h-[90vh] max-w-none max-h-none p-0 flex flex-col">
        {content}
      </DialogContent>
    </Dialog>
  );
}

